/**
 * The installment editor's pure half (P5-T2).
 *
 * Everything here is a pure function so the parts of an installment change that the plan writes
 * down — the count's legal range, the sign rule, **which fields a forward edit may carry**, and
 * why a member is outside a change's scope — are asserted in the fast suite instead of only being
 * exercised through a browser.
 *
 * Two rules from the plan's Accept line are enforced here rather than left to the API:
 *
 *   - a forward edit's fields are exactly `signedMinor`, `description` and `accountId`; the due
 *     date, the paid state and the notes are **not** in the body at all, so a date can never be
 *     "propagated" by accident (`INST-05`);
 *   - the count is refused outside `1..120` before a request is made, and a zero or negative
 *     total never produces a member (`INST-01`).
 *
 * The API enforces both again — it is the authority — and a body that slips past this is refused
 * there. This layer exists so the ordinary mistake is answered without a round trip.
 */

import type {
  InstallmentsCreateRequest,
  InstallmentsDeleteRequest,
  InstallmentsEditRequest,
  InstallmentExcludedBody,
  InstallmentMemberState,
  MutationPreviewBody,
} from '@saldo/contracts';

import { parseAmountToMinor } from './money';
import type { EntryForm, Mapped } from './forms';

/** The decision register's collection limits: 1..120 installments (`INST-01`). */
export const INSTALLMENT_MIN = 1;
export const INSTALLMENT_MAX = 120;

/** The two edit scopes the plan names: this member alone, or this member and its forward set. */
export type InstallmentScope = 'single' | 'forward';

export const INSTALLMENT_SCOPES: readonly InstallmentScope[] = ['single', 'forward'];

const DESCRIPTION_LIMIT = 500;

/**
 * The installment count typed into the editor.
 *
 * `1` is an ordinary entry — not a one-member group — so the caller decides that, and this only
 * answers whether the number itself is legal.
 */
export function parseInstallmentCount(value: string): Mapped<number> {
  const text = value.trim();
  if (!/^[0-9]+$/.test(text)) {
    return { ok: false, error: `Enter a whole number of installments (${INSTALLMENT_MIN}–${INSTALLMENT_MAX}).` };
  }
  const count = Number(text);
  if (count < INSTALLMENT_MIN || count > INSTALLMENT_MAX) {
    return { ok: false, error: `Enter between ${INSTALLMENT_MIN} and ${INSTALLMENT_MAX} installments.` };
  }
  return { ok: true, body: count };
}

/**
 * The `installments.create` preview request.
 *
 * The anchor is the purchase's own due date (`INST-03`): the caller passes the form's due date
 * through untouched, and no separate first-installment date is invented here. The body carries no
 * `paid` member on purpose — a cash installment starts unpaid and its own PATCH is what pays it,
 * and a card member has no writable paid state.
 */
export function toInstallmentsCreate(
  form: EntryForm,
  count: number,
): Mapped<InstallmentsCreateRequest> {
  if (!Number.isInteger(count) || count < INSTALLMENT_MIN || count > INSTALLMENT_MAX) {
    return { ok: false, error: `Enter between ${INSTALLMENT_MIN} and ${INSTALLMENT_MAX} installments.` };
  }

  const minor = parseAmountToMinor(form.amount, { allowNegative: false, allowZero: false });
  if (minor === null) return { ok: false, error: 'Enter a positive amount with at most two decimal places.' };

  const description = form.description.trim();
  if (description === '') return { ok: false, error: 'Enter a description.' };
  if (description.length > DESCRIPTION_LIMIT) {
    return { ok: false, error: `Keep the description under ${DESCRIPTION_LIMIT} characters.` };
  }
  if (form.accountId === '') return { ok: false, error: 'Choose an account.' };
  if (form.transactionDate === '') return { ok: false, error: 'Enter a valid transaction date (YYYY-MM-DD).' };
  if (form.dueDate === '') return { ok: false, error: 'Enter a valid due date (YYYY-MM-DD).' };

  const signedMinor = form.kind === 'expense' ? `-${minor}` : minor;
  return {
    ok: true,
    body: {
      action: 'installments.create',
      accountId: form.accountId,
      type: form.kind,
      signedMinor,
      description,
      transactionDate: form.transactionDate,
      dueDate: form.dueDate,
      count,
    },
  };
}

/** The fields a forward edit may carry — the writable set, and nothing else. */
export interface InstallmentEditFields {
  readonly signedMinor?: string;
  readonly description?: string;
  readonly accountId?: string;
}

/**
 * The fields this edit actually changes.
 *
 * Only a field that differs from the member's stored value is carried, which is what keeps a
 * description edit from flattening the amounts of the whole forward set: a field the user did not
 * touch is not in the body, so it is not written to the other members either.
 */
export function editFieldsFrom(
  form: EntryForm,
  entry: { readonly signedMinor: string; readonly description: string; readonly accountId: string },
): Mapped<InstallmentEditFields> {
  const fields: { signedMinor?: string; description?: string; accountId?: string } = {};

  const minor = parseAmountToMinor(form.amount, { allowNegative: false, allowZero: false });
  if (minor === null) return { ok: false, error: 'Enter a positive amount with at most two decimal places.' };
  const signedMinor = form.kind === 'expense' ? `-${minor}` : minor;
  if (signedMinor !== entry.signedMinor) fields.signedMinor = signedMinor;

  const description = form.description.trim();
  if (description === '') return { ok: false, error: 'Enter a description.' };
  if (description !== entry.description) fields.description = description;

  if (form.accountId !== entry.accountId) fields.accountId = form.accountId;

  if (Object.keys(fields).length === 0) {
    return { ok: false, error: 'Change at least one of the amount, the description or the account.' };
  }
  return { ok: true, body: fields };
}

/**
 * The `installments.edit` preview request.
 *
 * An empty fields object is refused as a request rather than sent: it would be a change that says
 * nothing, and the API's own schema refuses `minProperties: 1`.
 */
export function toInstallmentsEdit(
  groupId: string,
  fromEntryId: string,
  scope: InstallmentScope,
  fields: InstallmentEditFields,
): Mapped<InstallmentsEditRequest> {
  const body: { signedMinor?: string; description?: string; accountId?: string } = {};
  if (fields.signedMinor !== undefined) body.signedMinor = fields.signedMinor;
  if (fields.description !== undefined) {
    const description = fields.description.trim();
    if (description === '') return { ok: false, error: 'Enter a description.' };
    if (description.length > DESCRIPTION_LIMIT) {
      return { ok: false, error: `Keep the description under ${DESCRIPTION_LIMIT} characters.` };
    }
    body.description = description;
  }
  if (fields.accountId !== undefined) body.accountId = fields.accountId;

  if (Object.keys(body).length === 0) {
    return { ok: false, error: 'Change at least one of the amount, the description or the account.' };
  }
  if (groupId === '' || fromEntryId === '') {
    return { ok: false, error: 'This entry does not belong to an installment group.' };
  }

  return { ok: true, body: { action: 'installments.edit', groupId, fromEntryId, scope, fields: body } };
}

/**
 * The `installments.delete` preview request.
 *
 * The selection is explicit ids and never "the whole group": a group is deleted by naming each of
 * its pending/future members, which is what keeps an effective or paid member out of reach.
 */
export function toInstallmentsDelete(
  groupId: string,
  entryIds: readonly string[],
): Mapped<InstallmentsDeleteRequest> {
  if (groupId === '') return { ok: false, error: 'This entry does not belong to an installment group.' };
  const unique = [...new Set(entryIds)];
  if (unique.length === 0) return { ok: false, error: 'Choose at least one installment to delete.' };
  return { ok: true, body: { action: 'installments.delete', groupId, entryIds: unique } };
}

/** Why a member sits outside the previewed change, in the plan's own words. */
const EXCLUSION_LABELS: Record<InstallmentExcludedBody['reason'], string> = {
  earlier: 'Earlier than this installment',
  'paid-cash': 'Already paid (cash)',
  'card-effective': 'Already effective on a card',
  'already-deleted': 'Already deleted',
};

export function exclusionLabel(reason: InstallmentExcludedBody['reason']): string {
  return EXCLUSION_LABELS[reason];
}

/** A member's own state, as the read reports it. */
const STATE_LABELS: Record<InstallmentMemberState, string> = {
  pending: 'Pending',
  future: 'Future',
  paid: 'Paid',
  effective: 'Effective',
  deleted: 'Deleted',
};

export function memberStateLabel(state: InstallmentMemberState): string {
  return STATE_LABELS[state];
}

/** One line of the previewed selection: the member as the preview would write it. */
export interface PreviewLine {
  readonly ordinal: number;
  readonly dueDate: string;
  readonly signedMinor: string;
  /** The existing row this preview changes, or `null` for a member that does not exist yet. */
  readonly entryId: string | null;
}

export function previewLines(preview: MutationPreviewBody): PreviewLine[] {
  return preview.members.map((member) => ({
    ordinal: member.ordinal,
    dueDate: member.dueDate,
    signedMinor: member.signedMinor,
    entryId: member.entryId ?? null,
  }));
}

export function previewExclusions(
  preview: MutationPreviewBody,
): { readonly entryId: string; readonly label: string }[] {
  return preview.excluded.map((excluded) => ({
    entryId: excluded.entryId,
    label: exclusionLabel(excluded.reason),
  }));
}

/**
 * What the editor needs to know about the group a member belongs to.
 *
 * The revision is here because a commit must state the group revision it read (`If-Match`), and
 * `scheduledCount` is what makes "of 12" truthful after a deletion — the count does not fall.
 */
export interface InstallmentGroupView {
  readonly id: string;
  readonly revision: string;
  readonly scheduledCount: number;
  readonly activeCount: number;
  readonly memberIds: readonly string[];
}

export function groupView(group: {
  id: string;
  revision: string;
  scheduledCount: number;
  activeCount: number;
  members: readonly { entryId: string }[];
}): InstallmentGroupView {
  return {
    id: group.id,
    revision: group.revision,
    scheduledCount: group.scheduledCount,
    activeCount: group.activeCount,
    memberIds: group.members.map((member) => member.entryId),
  };
}

/** One line of the preview panel, ready to render. */
export interface InstallmentPreviewLine {
  readonly ordinal: number;
  readonly label: string;
  readonly dueDate: string;
  readonly signedMinor: string;
  readonly currency: string;
  /** `null` for a member the preview would create. */
  readonly entryId: string | null;
}

/** The stored preview, as the panel renders it. */
export interface InstallmentPreviewView {
  readonly id: string;
  readonly action: MutationPreviewBody['action'];
  readonly scheduledCount: number;
  readonly activeCount: number;
  readonly totalMinor: string;
  readonly currency: string;
  readonly businessToday: string;
  readonly lines: readonly InstallmentPreviewLine[];
  readonly exclusions: readonly {
    readonly entryId: string;
    readonly reason: InstallmentExcludedBody['reason'];
    readonly label: string;
  }[];
}

export function previewView(preview: MutationPreviewBody): InstallmentPreviewView {
  return {
    id: preview.id,
    action: preview.action,
    scheduledCount: preview.scheduledCount,
    activeCount: preview.activeCount,
    totalMinor: preview.total.minorUnits,
    currency: preview.total.currency,
    businessToday: preview.businessToday,
    lines: preview.members.map((member) => ({
      ordinal: member.ordinal,
      label: `Installment ${member.ordinal} of ${preview.scheduledCount}`,
      dueDate: member.dueDate,
      signedMinor: member.signedMinor,
      currency: member.currency,
      entryId: member.entryId ?? null,
    })),
    exclusions: preview.excluded.map((excluded) => ({
      entryId: excluded.entryId,
      reason: excluded.reason,
      label: exclusionLabel(excluded.reason),
    })),
  };
}
